/**
 * FieldSearch Component
 *
 * Search input for filtering fields in the picker.
 * Features debounced input and clear button.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FieldSearchProps } from './types';

const DEBOUNCE_MS = 150;

export function FieldSearch({ value, onChange, placeholder, onClear }: FieldSearchProps) {
  const { t } = useTranslation('common');
  const resolvedPlaceholder =
    placeholder ?? t('fieldPicker.searchFields', 'Search fields...');
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local value with external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the callback
    debounceRef.current = setTimeout(() => {
      onChange(newValue);
    }, DEBOUNCE_MS);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
    onClear?.();
    inputRef.current?.focus();
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className="sticky top-0 z-10 px-2 py-2 bg-bg-elevated border-b border-border">
      <div className="relative">
        <Search
          className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-fg-muted pointer-events-none"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={resolvedPlaceholder}
          className={cn(
            'w-full pl-8 pr-8 py-2 text-sm rounded-input',
            'border border-border bg-bg text-fg placeholder:text-fg-muted',
            'focus:ring-2 focus:ring-primary/20 focus:border-primary focus:outline-none',
            'transition-colors'
          )}
          aria-label={t('fieldPicker.searchFields', 'Search fields')}
        />
        {localValue && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2',
              'p-0.5 rounded text-fg-muted hover:text-fg hover:bg-bg-muted',
              'transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
            )}
            aria-label={t('fieldPicker.clearSearch', 'Clear search')}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

export default FieldSearch;
